// noinspection JSUnusedGlobalSymbols

import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { createElement } from "react";
import { queryClient } from "@/app/providers/QueryProvider";
import { AuthGuard } from "@/auth/AuthGuard";
import { PERMISSIONS, userHasPermission } from "@/auth/permissions";
import { sessionQueryOptions } from "@/auth/session";
import { useAuthStore } from "@/auth/store";
import { permissionsQueryOptions, rolesQueryOptions } from "@/hooks/useAdmin";
import { commissionConfigQueryOptions } from "@/hooks/useCommissions";
import {
  agentStatsQueryOptions,
  statsQueryOptions,
} from "@/hooks/useDashboard";
import { profileQueryOptions } from "@/hooks/useSettings";
import { AgentDetailPage } from "@/pages/AgentDetailPage";
import { AgentHandbookPage } from "@/pages/AgentHandbookPage";
import { AuditLogPage } from "@/pages/AuditLogPage";
import { ClientsPage } from "@/pages/ClientsPage";
import { CommissionsPage } from "@/pages/CommissionsPage";
import { CompanyResultsPage } from "@/pages/CompanyResultsPage";
import { ContestsPage } from "@/pages/ContestsPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { ErrorPage } from "@/pages/ErrorPage";
import { ForbiddenPage } from "@/pages/ForbiddenPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ImportPage } from "@/pages/ImportPage";
import { LoginPage } from "@/pages/LoginPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { PublicConsentPage } from "@/pages/PublicConsentPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { RolesPage } from "@/pages/RolesPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { SettlementsPage } from "@/pages/SettlementsPage";
import { TendersPage } from "@/pages/TendersPage";
import { UsersPage } from "@/pages/UsersPage";
import type { User } from "@/types/auth";
import type { PermissionString } from "@/types/rbac";
import type { SettingsTab } from "@/types/settings";

const SETTINGS_TABS: SettingsTab[] = [
  "profile",
  "security",
  "notifications",
  "personalization",
  "privacy",
];

const rootRoute = createRootRoute({
  notFoundComponent: NotFoundPage,
});

/**
 * Guest-only guard: redirects authenticated users away from login/password pages.
 * Uses ensureQueryData to check if a valid session exists (cookie-based BFF).
 * On cold load isAuthenticated is always false, so we must check the server.
 */
async function redirectIfAuthenticated() {
  const hasSession = await queryClient
    .ensureQueryData(sessionQueryOptions())
    .then(() => true)
    .catch(() => false);

  if (hasSession) {
    useAuthStore.getState().setAuthenticated();
    throw redirect({ to: "/" });
  }

  useAuthStore.getState().clearAuth();
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  beforeLoad: redirectIfAuthenticated,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
  beforeLoad: redirectIfAuthenticated,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
  beforeLoad: redirectIfAuthenticated,
});

/**
 * Authenticated layout route — the central auth gate.
 *
 * beforeLoad runs BEFORE any child beforeLoad/loader/component.
 * It verifies the session via ensureQueryData (uses TanStack Query cache,
 * so subsequent navigations are instant) and passes the user to children
 * via the returned context.
 */
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  component: AuthGuard,
  beforeLoad: async () => {
    try {
      const user = await queryClient.ensureQueryData(sessionQueryOptions());
      useAuthStore.getState().setAuthenticated();
      return { user };
    } catch {
      useAuthStore.getState().clearAuth();
      throw redirect({ to: "/login" });
    }
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/",
  component: DashboardPage,
  loader: ({ context }: { context: { user: User } }) => {
    if (context.user.role === "Administrator") {
      return queryClient.ensureQueryData(statsQueryOptions());
    }
    // Agents have their own payouts; the partner role has none and loads the results itself.
    return userHasPermission(context.user, PERMISSIONS.SETTLEMENTS.READ)
      ? queryClient.ensureQueryData(agentStatsQueryOptions())
      : undefined;
  },
});

const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/settings",
  component: SettingsPage,
  validateSearch: (search: Record<string, unknown>): { tab: SettingsTab } => ({
    tab: SETTINGS_TABS.includes(search.tab as SettingsTab)
      ? (search.tab as SettingsTab)
      : "profile",
  }),
  loader: () => queryClient.ensureQueryData(profileQueryOptions()),
});

/**
 * Permission guard for child routes.
 * Reads user from parent route context — no network call needed,
 * because authenticatedRoute.beforeLoad already verified the session
 * and cached the user data.
 */
function requirePermission(permission: PermissionString) {
  return ({
    context,
    location,
  }: {
    context: { user: User };
    location: { pathname: string };
  }) => {
    if (!userHasPermission(context.user, permission)) {
      throw redirect({ to: "/403", search: { from: location.pathname } });
    }
  };
}

const agentsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/agents",
  component: UsersPage,
  beforeLoad: requirePermission(PERMISSIONS.USERS.READ),
  loader: () => queryClient.ensureQueryData(rolesQueryOptions()),
});

const agentDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/agents/$agentId",
  component: function AgentDetailRoute() {
    const { agentId } = agentDetailRoute.useParams();
    return createElement(AgentDetailPage, { agentId, key: agentId });
  },
  beforeLoad: requirePermission(PERMISSIONS.USERS.READ),
});

/** The list used to be "Użytkownicy" at /users — keep old bookmarks working. */
const legacyUsersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/users",
  beforeLoad: () => {
    throw redirect({ to: "/agents" });
  },
});

const rolesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/roles",
  component: RolesPage,
  beforeLoad: requirePermission(PERMISSIONS.ROLES.READ),
  loader: () =>
    Promise.all([
      queryClient.ensureQueryData(rolesQueryOptions()),
      queryClient.ensureQueryData(permissionsQueryOptions()),
    ]),
});

const commissionsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/commissions",
  component: CommissionsPage,
  beforeLoad: requirePermission(PERMISSIONS.SETTLEMENTS.UPDATE),
  loader: () => queryClient.ensureQueryData(commissionConfigQueryOptions()),
});

const settlementsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/settlements",
  component: SettlementsPage,
  beforeLoad: requirePermission(PERMISSIONS.SETTLEMENTS.READ),
});

const importRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/import",
  component: ImportPage,
  beforeLoad: requirePermission(PERMISSIONS.SETTLEMENTS.CREATE),
});

const companyResultsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/results",
  component: CompanyResultsPage,
  beforeLoad: requirePermission(PERMISSIONS.REPORTS.READ),
});

const clientsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/clients",
  component: ClientsPage,
  beforeLoad: requirePermission(PERMISSIONS.CLIENTS.READ),
});

const handbookRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/handbook",
  component: AgentHandbookPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    tool?: "contacts" | "documents" | "owu" | "search" | "termination";
  } => ({
    tool: ["contacts", "documents", "owu", "search", "termination"].includes(
      String(search.tool),
    )
      ? (search.tool as
          | "contacts"
          | "documents"
          | "owu"
          | "search"
          | "termination")
      : undefined,
  }),
  beforeLoad: requirePermission(PERMISSIONS.TOOLKIT.READ),
});

const auditRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/audit",
  component: AuditLogPage,
  beforeLoad: requirePermission(PERMISSIONS.AUDIT.READ),
});

const contestsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/contests",
  component: ContestsPage,
  beforeLoad: requirePermission(PERMISSIONS.CONTESTS.READ),
});

const tendersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/tenders",
  component: TendersPage,
  validateSearch: (search: Record<string, unknown>): { id?: string } => ({
    id: typeof search.id === "string" ? search.id : undefined,
  }),
  beforeLoad: ({ context, location }) => {
    if (context.user.role !== "Administrator") {
      throw redirect({ to: "/403", search: { from: location.pathname } });
    }
  },
});

/** Public: the client's one-time RODO link from the SMS/e-mail. No session required. */
const publicConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/zgoda/$token",
  component: function PublicConsentRoute() {
    const { token } = publicConsentRoute.useParams();
    return createElement(PublicConsentPage, { token });
  },
});

const forbiddenRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/403",
  component: ForbiddenPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  publicConsentRoute,
  authenticatedRoute.addChildren([
    dashboardRoute,
    agentsRoute,
    agentDetailRoute,
    legacyUsersRoute,
    rolesRoute,
    commissionsRoute,
    settlementsRoute,
    importRoute,
    clientsRoute,
    handbookRoute,
    auditRoute,
    contestsRoute,
    tendersRoute,
    companyResultsRoute,
    settingsRoute,
    forbiddenRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultErrorComponent: ErrorPage,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
